import React from "react";
import { motion } from "framer-motion";
import { MapPin, Building, Ruler, Clock, RotateCcw, Satellite, Footprints, Timer } from "lucide-react";
import { useTranslation } from "react-i18next";

const LocationWidget: React.FC = () => {
    const { t } = useTranslation("nonHealth");
    
    const [locationData, setLocationData] = React.useState({
        speed: 3.2,
        duration: 45,
        accuracy: 5,
        updateTime: "justNow"
    });

    React.useEffect(() => {
        // Simulate dynamic data updates
        const interval = setInterval(() => {
            setLocationData(prev => ({
                speed: +(Math.random() * 5 + 1).toFixed(1),
                duration: prev.duration + 1,
                accuracy: Math.floor(Math.random() * 5) + 3,
                updateTime: "justNow"
            }));
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    return (
        <motion.div
            key="location-widget-enhanced"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            transition={{
                duration: 0.6,
                type: "spring",
                stiffness: 120,
                damping: 15,
            }}
        >
            <div className="bg-gradient-to-br from-white/90 to-white/80 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-sky-200/70 overflow-hidden">
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                        <MapPin className="text-rose-500 w-5 h-5" />
                        <h5 className="text-sky-800 font-semibold text-lg">{t("widgets.location.title")}</h5>
                    </div>
                    <motion.span
                        className="text-xs bg-gradient-to-r from-green-500 to-emerald-600 text-white px-3 py-1.5 rounded-full font-medium shadow-sm"
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "mirror",
                        }}
                    >
                        <Satellite className="w-3 h-3 inline mr-1" /> {t("widgets.location.gpsActive")}
                    </motion.span>
                </div>

                {/* نقشه تعاملی */}
                <motion.div
                    className="relative h-52 mb-4 overflow-hidden rounded-xl bg-gradient-to-br from-sky-50 to-indigo-100 border border-sky-200 shadow-inner cursor-grab active:cursor-grabbing group"
                    whileHover={{
                        scale: 1.03,
                        boxShadow: "0 0 20px rgba(14, 165, 233, 0.4)",
                    }}
                    drag
                    dragConstraints={{
                        top: -15,
                        left: -15,
                        right: 15,
                        bottom: 15,
                    }}
                    transition={{ duration: 0.4 }}
                >
                    <motion.div className="absolute inset-0 bg-sky-100/30 opacity-0 group-active:opacity-100 transition-opacity duration-300 z-0" style={{ borderRadius: "inherit" }} />
                    <motion.div className="absolute inset-2 bg-sky-50/50 opacity-0 group-active:opacity-100 transition-opacity duration-300 delay-100 z-0" style={{ borderRadius: "0.75rem" }} />

                    {/* Pulsing GPS Signal */}
                    <motion.div className="absolute inset-0 flex items-center justify-center z-0">
                        {[...Array(3)].map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute w-20 h-20 border-2 border-sky-500/60 rounded-full"
                                initial={{ scale: 0, opacity: 0.8 }}
                                animate={{ scale: 2.8 + i * 0.6, opacity: 0 }}
                                transition={{
                                    duration: 3.5,
                                    repeat: Infinity,
                                    delay: i * 0.8,
                                    ease: "circOut",
                                }}
                            />
                        ))}
                    </motion.div>

                    {/* مسیر حرکت */}
                    <svg className="absolute inset-0 z-1" viewBox="0 0 100 100">
                        <defs>
                            <linearGradient id="pathGradientEnhanced" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#fb7185" stopOpacity="0.9" />
                                <stop offset="50%" stopColor="#ea580c" stopOpacity="0.9" />
                                <stop offset="100%" stopColor="#2563eb" stopOpacity="0.9" />
                            </linearGradient>
                        </defs>
                        <motion.path
                            d="M15,75 Q30,60 45,68 T70,50 T85,60"
                            fill="none"
                            stroke="url(#pathGradientEnhanced)"
                            strokeWidth="2.5"
                            strokeDasharray="4,2"
                            initial={{ pathLength: 0, opacity: 0 }}
                            animate={{ pathLength: 1, opacity: 0.8 }}
                            transition={{ duration: 3, ease: "easeInOut", delay: 0.5 }}
                        />
                        {[
                            { cx: 15, cy: 75, color: "#fb7185" },
                            { cx: 45, cy: 68, color: "#ea580c" },
                            { cx: 85, cy: 60, color: "#2563eb" },
                        ].map((point, idx) => (
                            <motion.circle
                                key={idx}
                                cx={point.cx}
                                cy={point.cy}
                                r="3"
                                fill={point.color}
                                stroke="white"
                                strokeWidth="1"
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{
                                    duration: 0.8,
                                    delay: 1 + idx * 0.5,
                                    type: "spring",
                                    stiffness: 200,
                                }}
                            />
                        ))}
                    </svg>

                    {/* Location Pin */}
                    <motion.div
                        className="absolute top-1/2 left-1/2 w-7 h-7 bg-gradient-to-br from-rose-500 to-red-600 rounded-full border-2 border-white shadow-xl flex items-center justify-center z-20 cursor-pointer"
                        style={{ x: "-50%", y: "-50%" }}
                        animate={{
                            scale: [1, 1.5, 1, 1.5, 1],
                            rotate: [0, 12, -12, 12, 0],
                            boxShadow: ["0 0 0 0 rgba(239, 68, 68, 0.7)", "0 0 0 12px rgba(239, 68, 68, 0)", "0 0 0 0 rgba(239, 68, 68, 0.7)"],
                        }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        whileHover={{
                            scale: 1.6,
                            rotate: 0,
                            boxShadow: "0 0 15px rgba(239, 68, 68, 0.5)",
                        }}
                        whileTap={{ scale: 1.4 }}
                    >
                        <MapPin className="text-white w-4 h-4" />
                    </motion.div>

                    {/* Grid Overlay */}
                    <svg viewBox="0 0 100 100" className="absolute inset-0 opacity-10 z-0">
                        {[...Array(10)].map((_, i) => (
                            <React.Fragment key={i}>
                                <motion.path
                                    d={`M${i * 10} 0 V100`}
                                    stroke="#3b82f6"
                                    strokeWidth="0.15"
                                    initial={{ pathLength: 0, opacity: 0 }}
                                    animate={{ pathLength: 1, opacity: 1 }}
                                    transition={{
                                        duration: 2,
                                        delay: i * 0.1 + 0.5,
                                        ease: "circOut",
                                    }}
                                />
                                <motion.path
                                    d={`M0 ${i * 10} H100`}
                                    stroke="#3b82f6"
                                    strokeWidth="0.15"
                                    initial={{ pathLength: 0, opacity: 0 }}
                                    animate={{ pathLength: 1, opacity: 1 }}
                                    transition={{
                                        duration: 2,
                                        delay: i * 0.1 + 0.5,
                                        ease: "circOut",
                                    }}
                                />
                            </React.Fragment>
                        ))}
                    </svg>

                    {/* اطلاعات مکان */}
                    <motion.div
                        className="absolute top-2.5 right-2.5 text-sky-800 text-xs bg-white/85 backdrop-blur-sm px-2.5 py-1.5 rounded-lg shadow-md flex items-center gap-1.5 cursor-default"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.8, ease: "easeOut" }}
                        whileHover={{
                            scale: 1.05,
                            backgroundColor: "rgba(255, 255, 255, 0.98)",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        }}
                    >
                        <motion.span
                            className="text-sm"
                            animate={{ rotate: [0, 8, -8, 0], scale: [1, 1.1, 1] }}
                            transition={{
                                duration: 4,
                                repeat: Infinity,
                                repeatType: "mirror",
                                delay: 2,
                                ease: "easeInOut",
                            }}
                        >
                            <Building className="w-4 h-4 text-sky-600" />
                        </motion.span>
                        <span className="font-medium">{t("widgets.location.locationAddress")}</span>
                    </motion.div>
                    <motion.div
                        className="absolute bottom-2.5 left-2.5 text-indigo-800 text-xs bg-white/85 backdrop-blur-sm px-2.5 py-1.5 rounded-lg shadow-md flex items-center gap-1.5 cursor-default"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 1, ease: "easeOut" }}
                        whileHover={{
                            scale: 1.05,
                            backgroundColor: "rgba(255, 255, 255, 0.98)",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        }}
                    >
                        <motion.span
                            className="text-sm"
                            animate={{ scale: [1, 1.2, 1, 1.2, 1] }}
                            transition={{
                                duration: 3.5,
                                repeat: Infinity,
                                repeatType: "mirror",
                                delay: 2.5,
                                ease: "backInOut",
                            }}
                        >
                            <Ruler className="w-4 h-4 text-indigo-600" />
                        </motion.span>
                        <span className="font-medium">{t("widgets.location.accuracy", { count: locationData.accuracy })}</span>
                    </motion.div>
                    <motion.div
                        className="absolute bottom-2.5 right-2.5 text-purple-800 text-xs bg-white/85 backdrop-blur-sm px-2.5 py-1.5 rounded-lg shadow-md flex items-center gap-1.5 cursor-default"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 1.2, ease: "easeOut" }}
                        whileHover={{
                            scale: 1.05,
                            backgroundColor: "rgba(255, 255, 255, 0.98)",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        }}
                    >
                        <motion.span className="text-sm" animate={{ rotate: [0, 360] }} transition={{ duration: 12, repeat: Infinity, ease: "linear" }}>
                            <Clock className="w-4 h-4 text-purple-600" />
                        </motion.span>
                        <span className="font-medium">{t("widgets.location.timeValue", { count: 15 })}</span>
                    </motion.div>
                </motion.div>

                {/* کارت‌های اطلاعاتی */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-gradient-to-br from-sky-100 to-blue-50 p-2 rounded-lg border border-sky-200">
                        <div className="text-xs text-sky-700 mb-1">{t("widgets.location.speed")}</div>
                        <div className="flex items-center justify-between">
                            <span className="text-sky-800 font-bold">{t("widgets.location.speedValue", { count: locationData.speed })}</span>
                            <span className="text-sky-500">
                                <Footprints className="w-4 h-4" />
                            </span>
                        </div>
                    </div>
                    <div className="bg-gradient-to-br from-indigo-100 to-purple-50 p-2 rounded-lg border border-indigo-200">
                        <div className="text-xs text-indigo-700 mb-1">{t("widgets.location.duration")}</div>
                        <div className="flex items-center justify-between">
                            <span className="text-indigo-800 font-bold">{t("widgets.location.timeValue", { count: locationData.duration })}</span>
                            <span className="text-indigo-500">
                                <Timer className="w-4 h-4" />
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center mb-3 text-sm">
                    <span className="text-gray-600 flex items-center gap-1">
                        <RotateCcw className="w-3 h-3" />
                        {t("widgets.location.lastUpdate")}
                    </span>
                    <motion.span
                        className="font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 px-2 py-0.5 rounded-md shadow-sm"
                        key={Date.now()}
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        {t(`widgets.location.${locationData.updateTime}`)}
                    </motion.span>
                </div>
            </div>
        </motion.div>
    );
};

export default LocationWidget;
